import { useEffect, useState } from "react";

import { Alert, AlertTitle, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";

import {
  ackAnnouncement,
  listActiveAnnouncements,
  type AnnouncementSeverity,
} from "@/api/announcements";

const SEVERITY_COLOR: Record<AnnouncementSeverity, "info" | "warning" | "error"> = {
  INFO: "info",
  WARNING: "warning",
  CRITICAL: "error",
};

const DISMISS_KEY = "cortex.announcements.dismissed";

function loadDismissed(): Set<string> {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as string[];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function saveDismissed(set: Set<string>): void {
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify([...set]));
  } catch {
    /* quota or disabled */
  }
}

export function AnnouncementBanner() {
  const [dismissed, setDismissed] = useState<Set<string>>(() => loadDismissed());

  const q = useQuery({
    queryKey: ["announcements-active"],
    queryFn: listActiveAnnouncements,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!q.data) return;
    const valid = new Set(q.data.map((a) => a.id));
    const next = new Set<string>();
    for (const id of dismissed) if (valid.has(id)) next.add(id);
    if (next.size !== dismissed.size) {
      setDismissed(next);
      saveDismissed(next);
    }
  }, [q.data, dismissed]);

  const rows = (q.data ?? []).filter((a) => !dismissed.has(a.id));
  if (rows.length === 0) return null;

  const handleClose = (id: string) => {
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    saveDismissed(next);
    ackAnnouncement(id).catch(() => {
      /* best-effort: dismissal works even if ack fails */
    });
  };

  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      {rows.map((a) => (
        <Alert
          key={a.id}
          severity={SEVERITY_COLOR[a.severity]}
          variant={a.severity === "CRITICAL" ? "filled" : "standard"}
          onClose={a.dismissible ? () => handleClose(a.id) : undefined}
        >
          <AlertTitle>{a.title}</AlertTitle>
          <Typography variant="body2" component="div" sx={{ whiteSpace: "pre-wrap" }}>
            {a.body}
          </Typography>
        </Alert>
      ))}
    </Stack>
  );
}
